import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';
import { BasicAction } from '../../../../../backend/src/core/basic-action';
import { CountResponse } from '../../../../../backend/src/core/request-response';
import { ResourceName } from '../../../../../backend/src/core/resource-name';
import {
  AddUserListToWorkspaceRequest,
  SetParentWorkspaceRequest,
  WorkspaceGate,
} from '../../../../../backend/src/core/workspace/workspace.gate';
import { WorkspacePublicGate } from '../../../../../backend/src/core/workspace/workspace.public.gate';
import { AsyncServiceBase } from '../../shared/core/async-service-base';
import { LambdaService } from '../../shared/core/lambda.service';
import { UserClerkService } from '../user/user-clerk.service';
import { Workspace, WorkspaceComboBackend } from './workspace';

@Injectable({
  providedIn: 'root',
})
export class WorkspaceService extends AsyncServiceBase<WorkspaceComboBackend, Workspace> {
  constructor(
    @Inject(HttpClient) http: HttpClient,
    @Inject(LambdaService) lambdaService: LambdaService,
    @Inject(UserClerkService) userClerkService: UserClerkService
  ) {
    super(http, lambdaService, userClerkService, ResourceName.workspace);
  }

  private workspaceChangeSubject = new BehaviorSubject<undefined>(undefined);

  get workspaceChange$() {
    return this.workspaceChangeSubject.asObservable();
  }

  setWorkspaceBackendToCachedData(backend: WorkspaceComboBackend) {
    return this.setOneItemToCacheData(backend);
  }

  deleteWorkspaceById$(workspaceId: string) {
    return this._lambdaService.run$(this._resourceName, WorkspaceGate.actions.deleteWorkspace, workspaceId).pipe(
      tap(() => {
        const itemList = this._itemOrItemList as Workspace[];
        const deleteIndex = itemList.findIndex((c) => c.id === workspaceId);
        itemList.splice(deleteIndex, 1);

        this.workspaceChangeSubject.next(undefined);
      })
    );
  }

  setParentWorkspace$(workspace: Workspace, parentId: string) {
    const request: SetParentWorkspaceRequest = {
      workspaceId: workspace.id,
      parentId,
    };
    return this._lambdaService.run$(this._resourceName, WorkspaceGate.actions.setParentWorkspace, request).pipe(
      tap(() => {
        workspace.parentId = parentId;
        this.workspaceChangeSubject.next(undefined);
      })
    );
  }

  addWorkspace$(workspace: { title: string }, setupCode?: string) {
    (workspace as any).setupCode = setupCode;
    (workspace as any).timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (!setupCode) {
      (workspace as any).needSetup = true;
    }

    return this.addItemPrivate$(workspace).pipe(tap((_) => this.workspaceChangeSubject.next(undefined)));
  }

  updateWorkspace$(workspace: Workspace) {
    return this.updateItemPrivate$(workspace, BasicAction.update).pipe(
      tap((data) => {
        this.workspaceChangeSubject.next(undefined);
      })
    );
  }

  addPendingHourlyTaskList$(workspace: Workspace) {
    return this.updateItemPrivate$(workspace, WorkspaceGate.actions.addPendingHourlyTaskList).pipe(
      tap(() => this.workspaceChangeSubject.next(undefined))
    );
  }

  removePendingHourlyTaskList$(workspace: Workspace) {
    return this.updateItemPrivate$(workspace, WorkspaceGate.actions.removePendingHourlyTaskList).pipe(
      tap(() => this.workspaceChangeSubject.next(undefined))
    );
  }

  getWorkspaceCount$(): Observable<number> {
    return this._lambdaService
      .run$(this._resourceName, WorkspacePublicGate.actions.getWorkspaceCount, undefined, {
        isPublicUrl: true,
      })
      .pipe(
        map((c: CountResponse) => {
          return c.count;
        })
      );
  }

  getMyWorkspaceList$(): Observable<Workspace[]> {
    return this.workspaceChange$.pipe(
      mergeMap(() => {
        return this.getItemOrItemListByAction$(WorkspaceGate.actions.getMyWorkspaceList) as Observable<Workspace[]>;
      }),
      map((workspaceList) => {
        workspaceList.sort((a, b) => a.title?.localeCompare(b.title));
        return workspaceList;
      })
    );
  }

  convertFromFrontendToBackend(frontend: Workspace): WorkspaceComboBackend {
    const backend = { ...frontend } as unknown as WorkspaceComboBackend;
    return backend;
  }

  convertFromBackendToFrontend(backend: WorkspaceComboBackend): Workspace {
    const frontend = { ...backend } as unknown as Workspace;

    // HyperFieldWorker.on(frontend, 'sk').assertNotEmpty();
    // HyperFieldWorker.on(frontend, 'createdTimeStr')
    //   .renameTo('createdTime')
    //   .map((t) => DateTime.fromISO(t).toLocal());

    return frontend as Workspace;
  }

  updateWorkspaceInCache(backend: WorkspaceComboBackend) {
    const workspace = this.setOneItemToCacheData(backend, true);
    this.workspaceChangeSubject.next(undefined);
    return workspace;
  }

  addUserListToWorkspace$(request: AddUserListToWorkspaceRequest) {
    if (!request.userList?.length) {
      return EMPTY;
    }

    return this._lambdaService.run$(this._resourceName, WorkspaceGate.actions.addUserListToWorkspace, request);
  }

  afterAddItem(frontend: Workspace): void {}
  afterDeleteItem(frontend: Workspace): void {}
  cleanTempData(): void {}
  afterUpdateItem(frontend: Workspace): void {}
}
